import { useState } from "react";
import { motion } from "framer-motion";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useLanguage } from "@/context/LanguageContext";
import { useSeo } from "@/lib/useSeo";
import { PAGE_SEO } from "@/data/pageSeo";
import { IMAGES } from "@/data/images";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Receipt, Users, CalendarClock, Building2, BarChart3, ShieldCheck } from "lucide-react";

const benefits = [
  { icon: Receipt, key: "b1" },
  { icon: CalendarClock, key: "b2" },
  { icon: Users, key: "b3" },
  { icon: ShieldCheck, key: "b4" },
  { icon: Building2, key: "b5" },
  { icon: BarChart3, key: "b6" },
];

export default function Corporate() {
  const { t, lang } = useLanguage();
  useSeo({ ...(PAGE_SEO.corporate[lang] || PAGE_SEO.corporate.en), path: PAGE_SEO.corporate.path, image: PAGE_SEO.corporate.image, lang });
  const [f, setF] = useState({ company_name: "", contact_name: "", email: "", phone: "", employees: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/corporate/inquiry", f);
      setDone(true);
      toast.success(t("corp.form.success"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const scrollForm = () => document.querySelector("#corp-form")?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="bg-background text-foreground overflow-x-hidden">
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[80vh] flex items-center">
        <div className="absolute inset-0 overflow-hidden">
          <img src={IMAGES.driving} alt="Corporate chauffeur transportation in Montréal" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/75 to-black/55" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 lg:px-10 pt-32 pb-16 text-center">
          <p className="text-xs tracking-luxe uppercase text-white/60 mb-5">{t("corp.eyebrow")}</p>
          <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-white">{t("corp.title")}</h1>
          <p className="text-white/70 text-base sm:text-lg max-w-2xl mx-auto mt-6 leading-relaxed">{t("corp.subtitle")}</p>
          <Button data-testid="corp-hero-cta" className="rounded-full bg-white text-black hover:bg-white/85 h-14 px-10 text-base mt-9" onClick={scrollForm}>
            {t("corp.cta")}
          </Button>
        </div>
      </section>

      {/* Benefits */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <h2 className="font-serif font-light text-4xl sm:text-5xl text-white mb-14 max-w-2xl leading-tight">{t("corp.benefits.title")}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {benefits.map((b) => (
            <div key={b.key} className="rounded-xl border border-white/10 bg-card p-7" data-testid={`corp-benefit-${b.key}`}>
              <div className="h-11 w-11 rounded-full border border-white/20 flex items-center justify-center mb-5">
                <b.icon className="h-5 w-5 text-white" />
              </div>
              <h3 className="text-white text-lg font-medium mb-2">{t(`corp.${b.key}.t`)}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">{t(`corp.${b.key}.d`)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Form */}
      <section id="corp-form" className="border-t border-white/10 bg-card/30">
        <div className="max-w-2xl mx-auto px-6 lg:px-10 py-24">
          {done ? (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              className="text-center" data-testid="corp-success">
              <div className="h-16 w-16 rounded-full border border-white/20 bg-white/10 flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="h-8 w-8 text-white" />
              </div>
              <h2 className="font-serif font-light text-3xl text-white">{t("corp.form.success")}</h2>
              <p className="text-muted-foreground mt-4">{t("corp.form.successSub")}</p>
            </motion.div>
          ) : (
            <>
              <h2 className="font-serif font-light text-3xl sm:text-4xl text-white mb-2 text-center">{t("corp.form.title")}</h2>
              <p className="text-muted-foreground text-center mb-10">{t("corp.form.sub")}</p>
              <form onSubmit={submit} className="space-y-5" data-testid="corp-form">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label className="text-xs uppercase tracking-luxe text-muted-foreground">{t("corp.form.company")}</Label>
                    <Input data-testid="corp-company" required value={f.company_name} onChange={(e) => set("company_name", e.target.value)}
                      className="mt-2 h-12 bg-secondary border-white/10 text-white" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-luxe text-muted-foreground">{t("corp.form.contact")}</Label>
                    <Input data-testid="corp-contact" required value={f.contact_name} onChange={(e) => set("contact_name", e.target.value)}
                      className="mt-2 h-12 bg-secondary border-white/10 text-white" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-luxe text-muted-foreground">{t("booking.email")}</Label>
                    <Input data-testid="corp-email" type="email" required value={f.email} onChange={(e) => set("email", e.target.value)}
                      className="mt-2 h-12 bg-secondary border-white/10 text-white" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-luxe text-muted-foreground">{t("booking.phone")}</Label>
                    <Input data-testid="corp-phone" value={f.phone} onChange={(e) => set("phone", e.target.value)}
                      className="mt-2 h-12 bg-secondary border-white/10 text-white" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-luxe text-muted-foreground">{t("corp.form.employees")}</Label>
                  <Input data-testid="corp-employees" value={f.employees} onChange={(e) => set("employees", e.target.value)}
                    className="mt-2 h-12 bg-secondary border-white/10 text-white" placeholder="e.g. 25–100" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-luxe text-muted-foreground">{t("corp.form.message")}</Label>
                  <Textarea data-testid="corp-message" value={f.message} onChange={(e) => set("message", e.target.value)}
                    className="mt-2 bg-secondary border-white/10 text-white min-h-28" />
                </div>
                <Button data-testid="corp-submit" type="submit" disabled={loading}
                  className="w-full h-12 rounded-full bg-white text-black hover:bg-white/85">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("corp.form.submit")}
                </Button>
              </form>
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
